import React, { useState } from "react";
import { Input, Label, Box, Flex, Text } from "theme-ui";
import PropTypes from "prop-types";
import { typography, colors } from "../../styles/theme";
import Icon from "./Icon";

const iconBaseStyle = {
  position: "relative",
  backgroundColor: 'transparent',
  height: 'fit-content',
  alignSelf: 'center'
};

const styles = {
  inputStyles: {
    marginBottom: "0",
    ...typography.textMd400,
    color: colors.black,
    borderRadius: "8px",
    padding: "0.625rem 0.875rem",
    outline: "none",
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral300}`,
    minHeight: "3rem",
    "&:focus": {
      border: `2px solid ${colors.primaryMain}`,
      transition: "border-color 0.3s ease-in-out",
      minHeight: "2.875rem",
    },
    "&:disabled": {
      border: `1px solid ${colors.neutral300}`,
      backgroundColor: colors.neutral200,
      cursor: "not-allowed",
      color: colors.neutral500,
    },
    "&::placeholder": {
      ...typography.textMd400,
      color: colors.neutral700,
    },
  },
  labelStyles: {
    ...typography.textMd400,
    width: "auto"
  },
  relativePosition: {
    position: "relative",
  },
  leftIconStyle: {
    ...iconBaseStyle,
    left: "2.375rem",
  },
  rightIconStyle: {
    ...iconBaseStyle,
    right: "2.375rem",
  },
};

const InputBox = ({
  label,
  error,
  errorColor,
  type,
  name,
  value,
  disabled,
  minlength,
  maxlength,
  onChange,
  leftIconName,
  rightIconName,
  onClick,
  isRequired,
  placeholder,
  labelColor,
  eventHandlerText,
  eventHandler,
  customStyles,
  ...props
}) => {
  const {
    inputStyles,
    relativePosition,
    leftIconStyle,
    rightIconStyle,
    labelStyles,
  } = styles;

  return (
    <Box sx={{paddingBottom: error ? 0 : '1.031rem', ...customStyles}}>
      <Flex sx={{
        justifyContent: "space-between"
      }}>
      {label && !isRequired && <Label sx={{...labelStyles, color: labelColor}}>{label}</Label>}
      {label && isRequired && <Label sx={{...labelStyles, color: labelColor}}>{label}&nbsp;<Box sx={{color: colors.errorMain}}>*</Box></Label>}
      {eventHandlerText && <Box sx={{...typography.textMd500, color: colors.primaryMain, cursor: "pointer"}} onClick={eventHandler}>{eventHandlerText}</Box>}
      </Flex>
      <Flex sx={leftIconName || rightIconName ? relativePosition : {}}>
        {leftIconName && (
          <Box sx={leftIconStyle}>
            <Icon name={leftIconName} color={colors.neutral300} onClick={onClick} pointerCursor={true} />
          </Box>
        )}
        <Input
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          minLength={minlength}
          maxLength={maxlength}
          onChange={onChange}
          disabled={disabled}
          {...props}
          sx={{
            ...inputStyles,
            paddingRight: rightIconName ? "2.4rem" : "0.875rem",
            paddingLeft: leftIconName ? "2.4rem" : "0.875rem",
            borderColor: error ? colors.errorMain : colors.neutral300,
          }}
        />
        {(rightIconName || error) && (
          <Box sx={rightIconStyle}>
            <Icon
              name={error ? 'AlertTriangle' : rightIconName}
              color={error ? colors.errorMain : colors.neutral300}
              onClick={onClick}
              size={20}
              pointerCursor={true}
            />
          </Box>
        )}
      </Flex>
      {error && <Text sx={{...typography.textSm, color: errorColor}}>{error}</Text>}
    </Box>
  );
};

InputBox.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  errorColor: PropTypes.string,
  isRequired: PropTypes.bool,
  type: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  minlength: PropTypes.number,
  maxlength: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  leftIconName: PropTypes.string,
  rightIconName: PropTypes.string,
  onClick: PropTypes.func,
  customStyles: PropTypes.object,
};

InputBox.defaultProps = {
  error: "",
  errorColor: colors.errorMain,
  isRequired: false,
  type: "text",
  name: "",
  disabled: false,
  minlength: undefined,
  maxlength: undefined,
  placeholder: "Type here...",
  leftIconName: "",
  rightIconName: "",
  onClick: () => {},
  customStyles: {},
};

export default InputBox;
